/**
 * Values & Regulation Anchors
 * Keeps Life Vest, North Star, Anchor, Buoy within reach with editable prompts.
 */
export class ValuesAnchors {
  constructor(container, dataManager, options = {}) {
    this.container = container;
    this.data = dataManager;
    this.analytics = options.analytics || null;
    
    // Define templates FIRST before calling getAnchorsState
    this.anchorMeta = {
      lifeVest: {
        label: 'Life Vest',
        icon: '🛟',
        description: 'What keeps me afloat when everything feels wobbly.',
      },
      northStar: {
        label: 'North Star',
        icon: '⭐',
        description: 'Why this season matters; the direction I am moving toward.',
      },
      anchor: {
        label: 'Anchor',
        icon: '⚓',
        description: 'Support systems or rhythms that ground me.',
      },
      buoy: {
        label: 'Buoy',
        icon: '🦺',
        description: 'Small wins or reminders that pull me back when I drift.',
      },
    };

    this.templates = {
      lifeVest: [
        'Pause - Sip water - Three deep breaths - I can ramp up slowly.',
        'Text my check-in buddy and name one true thing.',
      ],
      northStar: [
        'Steady progress on my coaching practice so future me has more margin.',
        'Show up with compassion + clarity for myself and my people.',
      ],
      anchor: [
        'Morning meds + 10 min stretch + lo-fi playlist.',
        'Weekly review on Sunday evening with the dashboard open.',
      ],
      buoy: [
        'Celebrate one micro-win before logging off.',
        'Play my hype playlist after finishing a focus block.',
      ],
    };

    this.coachPrompts = [
      {
        id: 'breathing-box',
        title: 'Box Breathing Reset',
        prompt: 'Inhale for 4 - hold for 4 - exhale for 6. Repeat 3 rounds while relaxing your shoulders.',
        celebration: 'Breath anchored. Nice reset!',
      },
      {
        id: 'dropped-anchor',
        title: 'Dropped Anchor',
        prompt: 'Notice 3 things you can see, 3 you can touch, 3 you can hear. Say I am allowed to slow this moment down.',
        celebration: 'Grounded! Nervous system says thank you.',
      },
      {
        id: 'movement-sprint',
        title: 'Movement Sprint',
        prompt: 'Set a 60-sec timer. Shake out hands, roll shoulders, march in place. Finish with a big exhale.',
        celebration: 'Mini movement break logged. Momentum unlocked.',
      },
      {
        id: 'environment-reset',
        title: 'Environment Reset',
        prompt: 'Tidy one square foot, dim harsh lights, add music or white noise. Return to the dashboard when ready.',
        celebration: 'Space cleared. Fresh chapter starts now.',
      },
      {
        id: 'self-coach',
        title: 'Compassion Script',
        prompt: 'Say out loud: Nothing is wrong with me. I get to restart. Text or voice memo a friend if it helps it land.',
        celebration: 'Message delivered. You are on the same team as yourself.',
      },
      {
        id: 'micro-plan',
        title: 'Micro-plan Loop',
        prompt: 'Name the tiniest next step + gather only the tool you need. Start a 5-minute timer and see what happens.',
        celebration: 'Micro-plan engaged. Momentum over perfection.',
      },
    ];

    this.activePrompts = this.pickCoachPrompts();

    // NOW call getAnchorsState after templates are defined
    this.state = this.getAnchorsState();

    this.unsubscribe = null;

    if (this.container && this.data) {
      this.initialize();
    } else {
      console.warn('[ValuesAnchors] Missing container or data manager');
    }
  }

  initialize() {
    this.render();
    this.subscribe();
    // expose for mobile/home quick actions
    if (typeof window !== 'undefined') {
      window.lifeValuesAnchors = this;
    }
    this.analytics?.track?.('values_anchor_render', { count: this.countPopulatedAnchors() });
  }

  subscribe() {
    if (typeof this.data.subscribe !== 'function') {
      return;
    }

    try {
      this.unsubscribe = this.data.subscribe('valuesAnchors', (next) => {
        this.state = this.mergeWithDefaults(next);
        this.render();
      });
    } catch (error) {
      console.warn('[ValuesAnchors] Failed to subscribe to data changes', error);
    }
  }

  getAnchorsState() {
    const stored = this.data?.get?.('valuesAnchors', {});
    return this.mergeWithDefaults(stored);
  }

  mergeWithDefaults(input = {}) {
    const defaults = {
      lifeVest: this.templates.lifeVest[0],
      northStar: this.templates.northStar[0],
      anchor: this.templates.anchor[0],
      buoy: this.templates.buoy[0],
      updatedAt: null,
    };

    return {
      ...defaults,
      ...(input || {}),
    };
  }

  render() {
    if (!this.container) return;
    this.container.innerHTML = `
      <div class="card card--nature values-anchors-card">
        <div class="card__header">
          <h3 class="card__title">
            <span class="emoji">🧭</span>
            Values & Regulation Anchors
          </h3>
          <button class="btn btn--small btn--ghost" data-action="open-first-aid">
            ADHD First Aid
          </button>
        </div>
        <div class="card__content">
          <p class="values-anchors__intro">
            These anchors keep your nervous system and priorities aligned. Update them as seasons shift.
          </p>
          <div class="values-anchors__list">
            ${this.renderAnchors()}
          </div>
          <div class="values-anchors__coach">
            <div class="values-anchors__coach-header">
              <h4>Coach nudges</h4>
              <button class="btn btn--tiny btn--ghost" data-action="shuffle-prompts">Shuffle</button>
            </div>
            <p class="values-anchors__coach-copy">Pick a regulation move to support your next transition:</p>
            <div class="values-anchors__prompt-grid">
              ${this.renderCoachPrompts()}
            </div>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
    this.analytics?.track?.('values_anchor_render', { count: this.countPopulatedAnchors() });
  }

  renderAnchors() {
    return Object.entries(this.anchorMeta)
      .map(([key, meta]) => {
        const text = (this.state[key] || '').trim();
        return `
          <section class="values-anchor" data-anchor="${key}">
            <header class="values-anchor__header">
              <div class="values-anchor__title">
                <span class="values-anchor__icon">${meta.icon}</span>
                <div>
                  <h4>${meta.label}</h4>
                  <p>${meta.description}</p>
                </div>
              </div>
              <button class="btn btn--tiny" data-action="edit" data-anchor="${key}">Edit</button>
            </header>
            <p class="values-anchor__body">
              ${text ? this.escapeHtml(text) : '<em>Add your words so this anchor shows up when you need it.</em>'}
            </p>
          </section>
        `;
      })
      .join('');
  }

  renderCoachPrompts() {
    if (!Array.isArray(this.activePrompts) || !this.activePrompts.length) {
      return '<p class="values-anchors__prompt-empty">We will refresh nudges the next time you visit.</p>';
    }

    return this.activePrompts
      .map((prompt) => {
        const meta = this.coachPrompts.find((item) => item.id === prompt);
        if (!meta) return '';
        return `
          <article class="values-anchors__prompt-card" data-prompt="${meta.id}">
            <h5>${this.escapeHtml(meta.title)}</h5>
            <p>${this.escapeHtml(meta.prompt)}</p>
            <button class="btn btn--tiny" data-action="apply-prompt" data-prompt-id="${meta.id}">Try this</button>
          </article>
        `;
      })
      .join('');
  }

  pickCoachPrompts(count = 3) {
    if (!Array.isArray(this.coachPrompts) || this.coachPrompts.length === 0) {
      return [];
    }
    const shuffled = [...this.coachPrompts]
      .sort(() => Math.random() - 0.5)
      .slice(0, count)
      .map((prompt) => prompt.id);
    return shuffled;
  }

  shuffleCoachPrompts() {
    this.activePrompts = this.pickCoachPrompts();
    this.analytics?.track?.('values_prompts_shuffled');
    this.render();
  }

  handlePromptSelection(promptId) {
    const prompt = this.coachPrompts.find((item) => item.id === promptId);
    if (!prompt) {
      return;
    }
    if (window.lifeDashboard?.showNotification) {
      window.lifeDashboard.showNotification(prompt.celebration || 'Logged!');
    }
    this.analytics?.track?.('values_prompt_acknowledged', { prompt: promptId });
  }

  bindEvents() {
    const editButtons = this.container.querySelectorAll('[data-action="edit"]');
    editButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const anchor = event.currentTarget.getAttribute('data-anchor');
        if (anchor) {
          this.openEditModal(anchor);
        }
      });
    });

    const firstAidBtn = this.container.querySelector('[data-action="open-first-aid"]');
    firstAidBtn?.addEventListener('click', () => {
      this.analytics?.track?.('values_first_aid_open');
      this.openFirstAidResources();
    });

    const shuffleButton = this.container.querySelector('[data-action="shuffle-prompts"]');
    shuffleButton?.addEventListener('click', () => {
      this.shuffleCoachPrompts();
    });

    const promptButtons = this.container.querySelectorAll('[data-action="apply-prompt"]');
    promptButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const { promptId } = event.currentTarget.dataset;
        if (promptId) {
          this.handlePromptSelection(promptId);
        }
      });
    });
  }

  openEditModal(anchorKey) {
    const meta = this.anchorMeta[anchorKey];
    if (!meta) return;

    const current = this.state[anchorKey] || '';

    this.analytics?.track?.('values_anchor_edit_open', { anchor: anchorKey });

    const modal = document.createElement('div');
    modal.className = 'values-anchor-modal';
    modal.innerHTML = `
      <div class="values-anchor-modal__backdrop"></div>
      <div class="values-anchor-modal__dialog" role="dialog" aria-modal="true">
        <header class="values-anchor-modal__header">
          <div>
            <h3>${meta.icon} ${meta.label}</h3>
            <p>${meta.description}</p>
          </div>
          <button class="values-anchor-modal__close" aria-label="Close" data-close="true">×</button>
        </header>
        <div class="values-anchor-modal__body">
          <label class="values-anchor-modal__label" for="anchor-textarea">${meta.label} words</label>
          <textarea id="anchor-textarea" class="values-anchor-modal__textarea" rows="5" placeholder="Write how this anchor supports you">${this.escapeHtml(
            current
          )}</textarea>

          <div class="values-anchor-modal__templates">
            <span>Templates for inspiration:</span>
            <div class="values-anchor-modal__chips">
              ${this.templates[anchorKey]
                .map(
                  (template) => `
                  <button class="values-anchor-chip" data-template="${this.escapeHtmlAttribute(template)}">
                    ${template}
                  </button>`
                )
                .join('')}
            </div>
          </div>
        </div>
        <footer class="values-anchor-modal__footer">
          <button class="btn btn--ghost" data-close="true">Cancel</button>
          <button class="btn btn--primary" data-action="save">Save Anchor</button>
        </footer>
      </div>
    `;

    document.body.appendChild(modal);

    const removeModal = () => {
      modal.querySelectorAll('button[data-close="true"]').forEach((btn) => {
        btn.removeEventListener('click', removeModal);
      });
      modal.querySelector('[data-action="save"]')?.removeEventListener('click', handleSave);
      modal.querySelectorAll('.values-anchor-chip').forEach((chip) => {
        chip.removeEventListener('click', handleTemplateClick);
      });
      modal.remove();
    };

    const handleSave = () => {
      const textarea = modal.querySelector('#anchor-textarea');
      const value = textarea?.value?.trim() || '';
      this.updateAnchor(anchorKey, value);
      this.analytics?.track?.('values_anchor_saved', { anchor: anchorKey });
      removeModal();
    };

    const handleTemplateClick = (event) => {
      const { template } = event.currentTarget.dataset;
      const textarea = modal.querySelector('#anchor-textarea');
      if (textarea && template) {
        textarea.value = template;
        this.analytics?.track?.('values_anchor_template_applied', { anchor: anchorKey });
      }
    };

    modal.querySelectorAll('button[data-close="true"]').forEach((btn) => {
      btn.addEventListener('click', removeModal);
    });
    modal.querySelector('[data-action="save"]')?.addEventListener('click', handleSave);
    modal.querySelectorAll('.values-anchor-chip').forEach((chip) => {
      chip.addEventListener('click', handleTemplateClick);
    });

    setTimeout(() => {
      modal.querySelector('#anchor-textarea')?.focus();
    }, 20);
  }

  openFirstAidResources() {
    window.open('https://adhdfirstaid.com/', '_blank', 'noopener');
  }

  updateAnchor(anchorKey, value) {
    const next = {
      ...this.state,
      [anchorKey]: value,
      updatedAt: new Date().toISOString(),
    };
    this.state = next;
    try {
      this.data.set('valuesAnchors', next);
    } catch (error) {
      console.warn('[ValuesAnchors] Failed to persist anchor data', error);
    }
    this.render();
    this.analytics?.track?.('values_anchor_render', { count: this.countPopulatedAnchors() });
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeHtmlAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }

  startQuickEdit(anchorKey = 'lifeVest') {
    this.analytics?.track?.('values_anchor_quick_edit', { anchor: anchorKey });
    this.openEditModal(anchorKey);
  }

  countPopulatedAnchors() {
    const keys = ['lifeVest', 'northStar', 'anchor', 'buoy'];
    return keys.filter((key) => (this.state?.[key] || '').trim()).length;
  }

  dispose() {
    if (this.unsubscribe) {
      try {
        this.unsubscribe();
      } catch (error) {
        console.warn('[ValuesAnchors] Failed to unsubscribe', error);
      }
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}
